import { Heading, Input, Separator, Text, toast } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectOrganizationById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/organization/")

export const OrganizationSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const organization = useAppSelector(selectOrganizationById(organizationId))

	if (!organization) {
		throw new Error(`Expected organization to be defined, but got ${organization}`)
	}

	const isCurrentUserOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handleOrganizationNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const organizationName = e.target.value

			if (!organizationName) {
				toast.error({
					title: "Organization name cannot be blank",
					description: "Please enter a valid organization name",
				})
				return
			}
			sdk.organizations.update({ ...organization, name: organizationName }).catch((error) => {
				toast.error({
					title: "Failed to update organization name",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[organization],
	)

	const organizationNameInputProps = useBlurInput(organization.name, handleOrganizationNameInputBlur, true)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Organization</Heading>
					<Text size="sm" accentColor="base">
						Manage your organization&apos;s settings.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex w-max flex-col gap-1">
					<Text size="sm" accentColor="base">
						Name
					</Text>
					<Input.Root variant="outline" accentColor="base" className="w-[300px]">
						<Input.Field
							placeholder="Enter a name"
							disabled={!isCurrentUserOrganizationAdmin}
							{...organizationNameInputProps}
						/>
					</Input.Root>
				</div>
			</section>
		</ViewContainer>
	)
})
OrganizationSettingsView.displayName = "OrganizationOverviewView"
